import React, { Component } from 'react';
import axios from 'axios';
import tradOrSimp from 'traditional-or-simplified';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import Crossword from '../components/Crossword/Crossword';
import Clues from '../components/Crossword/Clues/Clues';
import DoneButton from '../components/Crossword/DoneButton/DoneButton';
import PlayerControls from '../components/PlayerControls/PlayerControls';
import Dialog from '../components/Dialog/Dialog';
import SeeSolutionButton from '../components/SeeSolutionButton/SeeSolutionButton';
import LoginReminder from '../components/LoginReminder/LoginReminder';
import * as Utils from '../components/Crossword/Utils';
import { passCsrfToken, logPlayEvent, isLoggedIn } from '../utils/helpers';

dayjs.extend(duration);

const PROGRESS_SAVE_DEBOUNCE_MS = 1000;

class PuzzlePlayer extends Component {
  state = {
    puzzleId: null,
    solutionId: null,
    size: null,
    puzzle: [],
    translatedSolution: [],
    userAnswer: [],
    wrongAnswers: [],
    focusedCell: null,
    clues: {
      Across: [],
      Down: []
    },
    answers: {
      Across: [],
      Down: []
    },
    translatedAnswers: {
      Across: [],
      Down: []
    },
    title: '',
    user: {
      id: null,
      username: null
    },
    author: {
      id: null,
      username: null
    },
    puzzleComplete: false,
    gaveUp: false,
    characterSet: 'unknown',
    translatedCharacterSet: 'unknown',
    isTranslated: false,
    results: {
      startTime: 0,
      attempts: [],
      isStarted: false,
      isCompleted: false
    },
    modal: {
      isOpen: false,
      message: '',
      title: 'Notice',
      action: 'close',
      button: 'Close',
      buttonTwo: null,
      buttonTwoAction: null
    }
  };

  // Build a blank user-answer grid from the puzzle layout (`#` for blocks,
  // `''` for input cells). The puzzle layout has the same dimensions as the
  // solution but cells are clue numbers, null, or `#` rather than characters.
  buildBlankUserAnswer = (puzzleGrid) => {
    return puzzleGrid.map(row => row.map(cell => cell === '#' ? '#' : ''));
  };

  loadPuzzleData = () => {
    const matches = window.location.href.match(/\/play\/([0-9]+)/);
    const puzzleId = matches[1];
    const jsonPath = '/puzzles/' + puzzleId  + '.json';
    axios.get(jsonPath)
      .then(response => {
        if (response.status === 200) {
          const ipuz = JSON.parse(response.data.puzzle.body);
          const userAnswer = this.buildBlankUserAnswer(ipuz.puzzle);
          // Clues component expects answers to be parallel arrays to clues
          // ([clueNumber, answerText]). The play view is not editable, so the
          // answer text is never rendered — empty placeholders suffice.
          const answers = {
            Across: ipuz.clues.Across.map(c => [c[0], '']),
            Down: ipuz.clues.Down.map(c => [c[0], ''])
          };
          this.setState({
            size: ipuz.dimensions.width + 'x' + ipuz.dimensions.height,
            puzzleId: puzzleId,
            puzzle: ipuz.puzzle,
            userAnswer: userAnswer,
            answers: answers,
            clues: ipuz.clues,
            title: response.data.puzzle.title,
            author: {
              id: response.data.author.id,
              username: response.data.author.username
            },
            user: {
              id: response.data.user.id,
              username: response.data.user.username
            },
            characterSet: response.data.puzzle.character_set,
            translatedCharacterSet: response.data.puzzle.character_set
          }, this.startOrResumeSolution);
        } else {
          this.setState({
            modal: {
              isOpen: true,
              message: 'Problem loading puzzle',
              action: 'close',
              button: 'Close',
              title: 'Error'
            }
          });
        }
      })
      .catch(error => {
        let message = 'Problem loading puzzle: ' + error;
        if (error.response) {
          message = 'Problem loading puzzle: ' + JSON.stringify(error.response.data);
        }
        this.setState({
          modal: {
            isOpen: true,
            message: message,
            title: 'Error',
            action: 'close',
            button: 'Close'
          }
        });
      });
  };

  // Create or resume the in-progress solution row. Skipped for guests and
  // for the puzzle author (server returns 403, and there's no progress to save
  // anyway).
  startOrResumeSolution = () => {
    if (!isLoggedIn()) return;
    if (this.state.user.id === null || this.state.author.id === null) return;
    if (this.state.user.id === this.state.author.id) return;

    axios.post('/solutions.json', { solution: { puzzle: this.state.puzzleId } })
      .then(response => {
        if (response.status !== 200 && response.status !== 201) return;
        const solution = response.data;
        const newState = { solutionId: solution.id };
        if (solution.body) {
          try {
            newState.userAnswer = JSON.parse(solution.body);
          } catch (e) {
            // Ignore corrupt body — start fresh.
          }
        }
        if (solution.status === 'completed') {
          newState.results = {
            ...this.state.results,
            isCompleted: true,
            isStarted: true
          };
          newState.gaveUp = !!solution.gave_up;
        }
        this.setState(newState);
      })
      .catch(() => {
        // Non-fatal: progress just won't be saved.
      });
  };

  loadTranslated = () => {
    const translationPath = 'https://api-prod.zhxword.com/t2s/' + encodeURI(JSON.stringify(this.state.answers));
    axios.get(translationPath)
      .then(response => {
        if (response.status === 200) {
          const translatedAnswers = JSON.parse(response.data.message);
          const translatedSolution = Utils.buildSolutionFromAnswers(translatedAnswers, this.state.puzzle, this.state.size);
          this.setState({
            translatedAnswers: translatedAnswers,
            translatedSolution: translatedSolution
          });
        } else {
          this.setState({
            modal: {
              isOpen: true,
              message: 'Problem translating puzzle',
              action: 'close',
              button: 'Close',
              title: 'Error'
            }
          });
        }
      })
      .catch(error => {
        let message = 'Problem translating puzzle: ' + error;
        if (error.response) {
          message = 'Problem translating puzzle: ' + JSON.stringify(error.response.data);
        }
        this.setState({
          modal: {
            isOpen: true,
            message: message,
            title: 'Error',
            action: 'close',
            button: 'Close'
          }
        });
      });
  };

  updateCell = (cellKey, obj, newValue) => {
    const address = Utils.parseCellKey(cellKey);
    obj[address.row][address.col] = newValue;
  };

  isComplete = (solution) => {
    let isComplete = true;
    solution.map(row => {
      row.map(cell => {
        if (cell === '') {
          isComplete = false;
        }
      });
    });
    return isComplete
  };

  filterKeyboardInput = (raw) => {
    // Space must be allowed here, because of pinyin input behavior
    return raw.split('')
        .filter(char => /[a-zA-Z0-9 \p{Script=Han}üÜ]/u.test(char))
        .join('');
  };

  scheduleProgressSave = () => {
    if (!this.state.solutionId) return;
    if (this.state.results.isCompleted) return;
    if (this._saveTimer) {
      clearTimeout(this._saveTimer);
    }
    this._saveTimer = setTimeout(this.saveProgress, PROGRESS_SAVE_DEBOUNCE_MS);
  };

  saveProgress = () => {
    this._saveTimer = null;
    if (!this.state.solutionId) return;
    if (this.state.results.isCompleted) return;
    const payload = {
      solution: {
        body: JSON.stringify(this.state.userAnswer)
      }
    };
    axios.patch('/solutions/' + this.state.puzzleId + '.json', payload)
      .catch(() => {
        // Non-fatal: next debounce will retry.
      });
  };

  handleCellChange = (e) => {
    const newValue = this.filterKeyboardInput(e.target.value);
    const cellKey = e.target.getAttribute('cellkey');
    const newSolution = JSON.parse(JSON.stringify(this.state.userAnswer));
    if (newValue === '' || newValue === ' ') {
      this.updateCell(cellKey, newSolution, '');
    } else {
      // Character
      if (Utils.isChinese(newValue)) {
        const lastCharDetected = tradOrSimp.detect(newValue) || 'unknown';
        if (lastCharDetected.detectedCharacters !== 'unknown'
          && lastCharDetected.detectedCharacters !== this.state.translatedCharacterSet
          && this.state.translatedCharacterSet !== 'unknown') {
          this.setState({
            modal: {
              isOpen: true,
              message: `You entered a ${lastCharDetected.detectedCharacters} character, but this puzzle is set to ${this.state.translatedCharacterSet}. Please <b>either</b> switch to using ${this.state.translatedCharacterSet} for input on your device, <b>or</b> use the "translate" feature to automatically switch the puzzle to ${lastCharDetected.detectedCharacters}.`,
              title: 'Character Set Mismatch',
              action: 'close',
              button: 'Close'
            }
          });
          this.updateCell(cellKey, newSolution, '');
        } else {
          this.updateCell(cellKey, newSolution, newValue);
        }
      } else {
        this.updateCell(cellKey, newSolution, newValue);
      }
    }
    const results = JSON.parse(JSON.stringify(this.state.results));
    if (!results.isStarted) {
      results.startTime = (new Date()).getTime(),
      results.isStarted = true;
    }
    this.setState({
      userAnswer: newSolution,
      results: results
    }, this.scheduleProgressSave);
  };

  handleInputFocus = (e) => {
    const focusedCell = e.target.getAttribute('cellkey');
    // Clear wrong state
    const coords = Utils.parseCellKey(focusedCell);
    const wrongAnswers = JSON.parse(JSON.stringify(this.state.wrongAnswers));
    if (wrongAnswers.length > 0) {
      wrongAnswers[coords.row][coords.col] = true;
    }
    this.setState({
      focusedCell: focusedCell,
      wrongAnswers: wrongAnswers
    });
  };

  handleInputBlur = (e) => {
    const userAnswer = Utils.sanitizeCells(JSON.parse(JSON.stringify(this.state.userAnswer)));
    this.setState({
      focusedCell: null,
      userAnswer: userAnswer
    });
  };

  handleCloseModal = () => {
    if (this.state.modal.action === 'close') {
      const modalState = {...this.state.modal};
      modalState.isOpen = false;
      this.setState({modal: modalState});
    } else {
      // Assume we go to that location
      window.location = this.state.modal.action;
    }
  };

  handleTranslate = (isTranslated, e) => {
    let newCharacterSet = this.state.characterSet;
    logPlayEvent('handleTranslate', {
      puzzleId: this.state.puzzleId,
      newCharacterSet: newCharacterSet
    });
    if (isTranslated) {
      if (this.state.characterSet === 'simplified') {
        newCharacterSet = 'traditional';
      } else if (this.state.characterSet === 'traditional') {
        newCharacterSet = 'simplified';
      }
    }
    this.loadTranslated();
    this.setState({
      isTranslated: isTranslated,
      translatedCharacterSet: newCharacterSet
    });
  };

  checkAnswers = () => {
    const timeTaken = this.state.results.isStarted
      ? (new Date()).getTime() - this.state.results.startTime
      : 0;
    const payload = {
      solution: {
        body: JSON.stringify(this.state.userAnswer),
        time: timeTaken
      }
    };
    axios.post('/solutions/' + this.state.puzzleId + '/complete.json', payload)
      .then(response => {
        if (response.status !== 200) return;
        const data = response.data;
        // Server returns true=correct cells; existing UI expects true=wrong-cell-cleared,
        // i.e. wrongAnswers[r][c] = true means the cell's wrong state should be cleared.
        // The existing compareNestedArrays returned true=match. We mirror that shape.
        const correctGrid = data.grid;

        const flattened = [].concat.apply([], correctGrid);
        const percentCorrect = Math.round(100 * flattened.reduce((n, val) => n + (val === true), 0) / flattened.length);
        logPlayEvent('checkAnswers', {
          puzzleId: this.state.puzzleId,
          percentCorrect: percentCorrect
        });
        const results = JSON.parse(JSON.stringify(this.state.results));
        results.attempts.push({
          attempttime: (new Date()).getTime(),
          percentCorrect: percentCorrect
        });

        if (!data.correct) {
          this.setState({
            wrongAnswers: correctGrid,
            results: results
          });
          return;
        }

        const dur = dayjs.duration(timeTaken);
        let timeStr = dur.hours() > 0 ? dur.hours() + ' hour(s),' : '';
        timeStr += dur.minutes() > 0 ? dur.minutes() + ' minute(s),' : '';
        timeStr += timeStr.length > 0 ? ' and ' : '';
        timeStr += dur.seconds() > 0 ? dur.seconds() + ' seconds' : '';
        results.isCompleted = true;

        let message = `You completed "${this.state.title}" in ${timeStr}, using ${results.attempts.length} attempt(s).`;
        if (!isLoggedIn()) {
          message += `<p class="login_reminder">Consider <a href="/users/sign_up">registering for an account</a> so that your score gets recorded. It's free and only takes a moment.</p>`
        }
        this.setState({
          wrongAnswers: correctGrid,
          modal: {
            isOpen: true,
            message: message,
            action: 'close',
            button: 'Close',
            title: 'Well Done!'
          },
          results: results
        });
      })
      .catch(error => {
        let message = 'Problem checking solution: ' + error;
        if (error.response) {
          message = 'Problem checking solution: ' + JSON.stringify(error.response.data);
        }
        this.setState({
          modal: {
            isOpen: true,
            message: message,
            title: 'Error',
            action: 'close',
            button: 'Close'
          }
        });
      });
  };

  isAuthor = () => {
    return this.state.user.id !== null
      && this.state.author.id !== null
      && this.state.user.id === this.state.author.id;
  };

  seeSolutionStart = () => {
    logPlayEvent('seeSolutionStart', {
      puzzleId: this.state.puzzleId
    });
    // Skip the confirmation when there's nothing to lose: the user already gave
    // up, or they're the author (their own puzzles aren't scored).
    if (this.state.gaveUp || this.isAuthor()) {
      this.seeSolution();
      return;
    }
    const message = `Once you peek at the solution, you won't be able to log a score for this puzzle. Do you want to proceed?`;
    this.setState({
      modal: {
        isOpen: true,
        message: message,
        action: 'close',
        button: 'Cancel',
        buttonTwo: 'Yes, I Want to Cheat',
        buttonTwoAction: 'seeSolution',
        title: 'Are You Sure?'
      }
    });
  };

  seeSolution = () => {
    logPlayEvent('seeSolution', {
      puzzleId: this.state.puzzleId
    });
    const timeTaken = this.state.results.isStarted
      ? (new Date()).getTime() - this.state.results.startTime
      : 0;
    axios.post('/solutions/' + this.state.puzzleId + '/give_up.json', { solution: { time: timeTaken } })
      .then(response => {
        if (response.status !== 200) return;
        const revealedSolution = response.data.solution;
        const results = JSON.parse(JSON.stringify(this.state.results));
        results.isCompleted = true;
        this.setState({
          wrongAnswers: [],
          userAnswer: revealedSolution,
          results: results,
          gaveUp: true
        });
      })
      .catch(error => {
        let message = 'Problem revealing solution: ' + error;
        if (error.response) {
          message = 'Problem revealing solution: ' + JSON.stringify(error.response.data);
        }
        this.setState({
          modal: {
            isOpen: true,
            message: message,
            title: 'Error',
            action: 'close',
            button: 'Close'
          }
        });
      });
  };

  handleModalButtonTwoClick = (action, e) => {
    if (action === 'seeSolution') {
      this.seeSolution()
    }
  };

  componentDidMount() {
    passCsrfToken(document, axios);
    this.loadPuzzleData();
  }

  componentWillUnmount() {
    if (this._saveTimer) {
      clearTimeout(this._saveTimer);
      this._saveTimer = null;
    }
  }

  render() {
    return (
      <React.Fragment>
        <Dialog isOpen={this.state.modal.isOpen}
          onRequestClose={this.handleCloseModal}
          contentLabel={this.state.modal.title}
          button={this.state.modal.button}
          buttonTwo={this.state.modal.buttonTwo}
          buttonTwoAction={this.state.modal.buttonTwoAction}
          onButtonTwoClick={this.handleModalButtonTwoClick}
          appElement="puzzlePlayerApp">
          {this.state.modal.message}
        </Dialog>
        <Crossword visible={true}
          editable={false}
          playable={this.state.results.isCompleted !== true}
          puzzle={this.state.puzzle}
          size={this.state.size}
          solution={this.state.userAnswer}
          wrongAnswers={this.state.wrongAnswers}
          onInputChange={this.handleCellChange}
          onInputFocus={this.handleInputFocus}
          onInputBlur={this.handleInputBlur}
          focusedCell={this.state.focusedCell}/>
        <PlayerControls
          translatedCharacterSet={this.state.translatedCharacterSet}
          active={this.state.results.isCompleted !== true}
          isTranslated={this.state.isTranslated}
          onTranslate={this.handleTranslate} />
        <Clues visible={true}
          editable={false}
          clues={this.state.clues}
          answers={this.state.answers}
          isComplete={this.state.puzzleComplete} />
        <div className="playerButtons">
          <DoneButton active={this.state.results.isCompleted !== true}
            visible={!this.isAuthor()}
            buttonClicked={this.checkAnswers}
            classes={['crosswordCheckButton']}>
            Check Answers
          </DoneButton>
          <SeeSolutionButton isLoggedIn={isLoggedIn()}
            active={this.state.results.isCompleted !== true || this.state.gaveUp}
            buttonClicked={this.seeSolutionStart}>
          </SeeSolutionButton>
        </div>
        <LoginReminder isLoggedIn={isLoggedIn()} />
      </React.Fragment>
    );
  }
}

export default PuzzlePlayer;
